import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { seoApi } from "@/lib/seoApi";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

/**
 * Generic CRUD table for versioned SEO Studio entities.
 *
 * Props:
 *   title, subtitle           — page header
 *   testIdPrefix              — data-testid stem
 *   listUrl                   — GET url returning array of {id, data, current_version, is_active}
 *   createUrl                 — POST url
 *   updateUrl(id)             — PUT url builder
 *   deactivateUrl(id, reason) — POST url builder
 *   columns                   — [{key, label, render?}] — key is `data.<key>` unless render provided
 *   fields                    — [{key, label, type, options?, required?, placeholder?}]
 *   canonicalizeCreate(form)  — form → POST body
 *   canonicalizeUpdate(form)  — form → PUT body (adds change_reason)
 *   fromRow(row)              — row → form (for edit)
 *   emptyForm
 */
export default function CrudPage({
  title, subtitle, testIdPrefix,
  listUrl, createUrl, updateUrl, deactivateUrl,
  columns, fields, canonicalizeCreate, canonicalizeUpdate, fromRow, emptyForm,
}) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  async function load() {
    const r = await seoApi.get(listUrl);
    setRows(r.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  function openNew() { setEditing(null); setForm(emptyForm); setOpen(true); }
  function openEdit(row) { setEditing(row); setForm(fromRow(row)); setOpen(true); }

  async function save() {
    setBusy(true);
    try {
      if (editing) {
        await seoApi.put(updateUrl(editing.id), canonicalizeUpdate(form));
        toast.success("Updated");
      } else {
        await seoApi.post(createUrl, canonicalizeCreate(form));
        toast.success("Created");
      }
      setOpen(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  }

  async function deactivate(row) {
    if (!window.confirm("Deactivate this entry?")) return;
    try {
      await seoApi.post(deactivateUrl(row.id, "deactivated via admin UI"));
      toast.success("Deactivated"); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  }

  return (
    <div className="space-y-6" data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-neutral-500 text-sm mt-1">{subtitle}</p>
        </div>
        <Button onClick={openNew} data-testid={`${testIdPrefix}-new-btn`}>
          <Plus size={16} className="mr-1" /> New
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">No entries yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr className="border-b border-neutral-200">
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-6 py-3">{c.label}</th>
                  ))}
                  <th className="text-left px-4 py-3">v</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-neutral-100"
                      data-testid={`${testIdPrefix}-row-${row.id}`}>
                    {columns.map((c) => (
                      <td key={c.key} className="px-6 py-3">
                        {c.render ? c.render(row) : String(row.data[c.key] ?? "—")}
                      </td>
                    ))}
                    <td className="px-4 py-3">v{row.current_version}</td>
                    <td className="px-4 py-3">
                      {row.is_active
                        ? <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">active</Badge>
                        : <Badge variant="outline" className="bg-neutral-100 text-neutral-600 border-neutral-200">inactive</Badge>}
                    </td>
                    <td className="px-6 py-3 text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)}
                              data-testid={`${testIdPrefix}-edit-${row.id}`}><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => deactivate(row)}
                              disabled={!row.is_active}
                              data-testid={`${testIdPrefix}-deactivate-${row.id}`}><Trash2 size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "New"}</DialogTitle>
            <DialogDescription>All fields are canonical per SEO Studio architecture.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <FieldControl key={f.key} field={f}
                            value={form[f.key]}
                            onChange={(v) => setForm({ ...form, [f.key]: v })} />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy}
                    data-testid={`${testIdPrefix}-form-save`}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldControl({ field, value, onChange }) {
  const common = { id: field.key, "data-testid": `field-${field.key}` };
  if (field.type === "select") {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <select {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                className="mt-1 flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm">
          {(field.options || []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <Textarea {...common} rows={field.rows || 3} value={value ?? ""}
                  placeholder={field.placeholder}
                  onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div>
        <Label htmlFor={field.key}>{field.label}</Label>
        <Input {...common} type="number" value={value ?? ""}
               onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      <Label htmlFor={field.key}>{field.label}</Label>
      <Input {...common} value={value ?? ""}
             placeholder={field.placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
